/* eslint-disable unicorn/prevent-abbreviations */
import { ensureNotNil } from '@studio/js-utils';
import { normalizeId } from '@studio/models';
import { Status } from '@studio/models/control/calibration';
import { filterTransients, usePendingDeviceIds } from '@studio/models/device';
import type { Device } from '@studio/models/device';
import type { Tuple } from '@studio/react-utils';
import type { EqualityFn, SelectorFn } from '@studio/react-utils/store';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { dequal } from 'dequal/lite';
import { useCallback, useContext, useEffect, useState } from 'react';
import type { Room, UseDeviceIds } from './types.js';
import { locateRoom } from './utils.js';
import { Slicer, useDepot } from '#~/depot/hooks.js';
// Transitory-placeholder slice (W2, spec §2.1). `useDeviceIds` merges
// its ids into `videoIds` alongside `pendingDevices` so an unauthenticated
// bulk-add camera gets a canvas tile immediately (spec §3.1).
import { useTransitoryDeviceIds } from '#~/device/transitory.js';
import { depot } from '#~/depot/index.js';
import { usePreferences, useProducer } from '#~/preferences/hooks.js';
import { useArrangementPref } from '#~/video/hooks.js';
import { Arrangement } from '#~/video/index.js';

// eslint-disable-next-line
import { Context } from './provider/context.js';
import { locateProxy } from '#~/agent/utils.js';
import { useSelectedVideoSync } from './selected-video-sync.js';
import { useHasSeenAgentSetup, useLastRoomIdPref } from './hook_last_id.js';
import { AgentRegistryMembershipRole } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import { ipControllerManager as joystick } from '#~/ipjoystick/index.js';

/**
 * @internal `useSelectedDevice` hook.
 *
 * @param type - Device type.
 * @returns Selected device ID and a function to set the preferred device.
 */
const useSelectedDevice = (type: 'audio' | 'video'): Tuple<T_Maybe<Device['id']>> => {
  const roomId = ensureNotNil(useContext(Context));
  const key = `room.${roomId}.${type}.selected`;
  const producer = useProducer();
  const deviceIds = useDeviceIds()[`${type}Ids`];
  const preferredId = normalizeId<Device['id']>(usePreferences((state) => state.getValue(key)));

  const [init, setInit] = useState(false);

  /** May be used before mount. Don't use `useLatestFn` */
  const setPreferredDevice = useCallback(
    (nextDevice: T_Maybe<Device['id']>, init: boolean = false) => {
      producer((draft) => {
        if (nextDevice === draft.getValue(key) && !init) {
          return;
        }

        draft.setValue(key, nextDevice);
      });
    },
    [key]
  );

  // Absence is temporary: Advanced Settings "Save" reloads a device via `replaceDevice` (remove +
  // re-add, same id), and the room mounts before State Sync delivers the list.
  // Per rule: absent-data-gates-permissively (ai.knowledge/global) — absence is not intent.
  const isPreferredUnavailable = !!preferredId && !deviceIds.includes(preferredId);

  // If no device is selected, or if the preferred device is not available, select the first (non-transient) device.
  const selectedId = preferredId && !isPreferredUnavailable ? preferredId : deviceIds[0];

  useSelectedVideoSync(roomId, selectedId, type === 'video');

  // Controls follow the DISPLAYED device. No de-dup guard: both sinks already ignore an unchanged
  // id, and a per-instance one could not dedupe across this hook's 14 call sites anyway.
  useEffect(() => {
    if (!selectedId || type !== 'video') {
      return;
    }

    locateProxy(selectedId)?.focusDevice(selectedId);
    joystick?.setDeviceID(selectedId);
  }, [selectedId, type]);

  // Persist only a real choice — the default for a room with no stored selection, or the stored
  // selection itself. A fallback shown while the preferred device is away is NOT persisted.
  if ((selectedId !== preferredId || !init) && !isPreferredUnavailable) {
    queueMicrotask(() => {
      setPreferredDevice(selectedId, !init);
      setInit(true);
    });
  }

  return [selectedId, setPreferredDevice];
};

/**
 * Hook that returns a function to release the stored selection for `type`.
 *
 * Per rule: absent-data-gates-permissively (ai.knowledge/global) — a transient absence must not
 * move the selection, so a confirmed delete is the one signal that may.
 *
 * @param type - Device type.
 * @param deviceId - Act only when this device is the stored one.
 * @param roomId - Required outside the room provider (the Advanced Settings dialog renders in the
 *   app-root portal). Resolve with `locateRoom` before the removal.
 * @returns Imperative function. No-ops if the room is unknown or the device is still listed.
 */
export const useResetSelectedDevice = (
  type: 'audio' | 'video'
): ((deviceId?: Device['id'], roomId?: Room['id']) => void) => {
  // Not `ensureNotNil`: this must survive being called from outside the room provider.
  const contextRoomId = useContext(Context);
  const producer = useProducer();

  return useCallback(
    (deviceId?: Device['id'], roomId?: Room['id']) => {
      const targetRoomId = roomId ?? contextRoomId;

      if (!targetRoomId) {
        return;
      }

      // Non-reactive read: this runs from a delete callback, not a render.
      const ids = filterTransients(depot.getState().rooms[targetRoomId]?.[`${type}Ids`] ?? []);

      if (
        deviceId &&
        ids.some(
          (existingDeviceId) =>
            normalizeId<Device['id']>(existingDeviceId) === normalizeId(deviceId)
        )
      ) {
        return;
      }

      const key = `room.${targetRoomId}.${type}.selected`;

      producer((draft) => {
        const selectedId = normalizeId<Device['id']>(draft.getValue(key));

        if (!selectedId) {
          return;
        }

        if (deviceId && selectedId !== normalizeId<Device['id']>(deviceId)) {
          return;
        }

        // A survivor, not `undefined`: clearing would need a mounted `useSelectedDevice` to refill.
        draft.setValue(
          key,
          ids.find(
            (remainingDeviceId) => normalizeId<Device['id']>(remainingDeviceId) !== selectedId
          )
        );
      });
    },
    [contextRoomId, type]
  );
};

/**
 * Hook to return the room-specific arrangement. The `useArrangementPref` hook returns the
 * _globally_ preferred arrangement, but the available arrangements is dependent on the number of
 * video sources in the room.
 *
 * @returns Room-specific video arrangement.
 */
export const useArrangement = (): ReturnType<typeof useArrangementPref> => {
  const [arrangement, setArrangement] = useArrangementPref();
  const { videoIds } = useDeviceIds();

  return [videoIds.length > 1 ? arrangement : Arrangement.Single, setArrangement];
};

/**
 * Returns the calibration status of the specified device.
 *
 * @param deviceId - Device ID.
 * @returns Calibration status or `undefined`.
 */
export const useCalibration = (deviceId: T_Maybe<Device['id']>): Status =>
  useDepot((depotState) => {
    if (!deviceId) {
      return Status.CALIBRATION_STATE_NULL;
    }

    const room = locateRoom(deviceId);

    if (!room) {
      return Status.CALIBRATION_STATE_NULL;
    }

    const device = depotState.rooms[room.id]?.devices[deviceId];

    if (!device) {
      return Status.CALIBRATION_STATE_NULL;
    }

    return device.control?.calibration?.status ?? Status.CALIBRATION_STATE_NULL;
  });

/**
 * Hook to return the _ordered_ IDs of the _non-transient_ video and audio devices in the current
 * control room. Note that this hook only reacts to changes in the set of device IDs, not to changes
 * in the devices themselves.
 *
 * @returns IDs of _non-transient_ video and audio devices.
 */
export function useDeviceIds(): UseDeviceIds {
  const pendingDeviceIds = usePendingDeviceIds();
  // Transitory placeholders (spec §2.1/§3.1) — merged into `videoIds`
  // via the SAME `appendAndDedup` seam used for `pendingDevices`, so the
  // placeholder tile renders "for free" the moment bulk-add records it.
  // `useTransitoryDeviceIds` subscribes with `shallow` equality, so this
  // only re-renders when the placeholder id SET changes. Scoped to this
  // Studio: a pending camera added to another Studio never shows here.
  const transitoryDeviceIds = useTransitoryDeviceIds(ensureNotNil(useContext(Context)));

  const appendAndDedup = (ids: Device['id'][]): Device['id'][] => {
    const combined = [
      ...ids,
      ...(Object.keys(pendingDeviceIds) as Device['id'][]),
      ...transitoryDeviceIds
    ];

    return Array.from(new Set(combined));
  };

  return useRoom(
    (room) => ({
      audioIds: filterTransients(room.audioIds),
      videoIds: appendAndDedup(filterTransients(room.videoIds))
    }),
    dequal
  );
}

export { useLastRoomIdPref, useHasSeenAgentSetup };

/**
 * Returns a reactive slice of the current control room.
 *
 * @param selectorFn - Slice selector.
 * @param equalityFn - Slice equality function.
 * @returns Selected slice.
 */
export function useRoom<Slice>(
  selectorFn: SelectorFn<Room, Slice>,
  equalityFn?: EqualityFn<Slice>
): Slice {
  const roomId = ensureNotNil(useContext(Context));

  return useDepot((depotState) => selectorFn(new Slicer(depotState).room(roomId)), equalityFn);
}

/**
 * Return the ID of the selected audio in the current control room.
 *
 * @returns Selected audio ID or `undefined` if no audio is selected.
 */
export const useSelectedAudio = (): Tuple<T_Maybe<Device['id']>> => useSelectedDevice('audio');

/**
 * Return the ID of the selected video in the current control room.
 *
 * @returns Selected video ID or `undefined` if no video is selected.
 */
export const useSelectedVideo = (): Tuple<T_Maybe<Device['id']>> => useSelectedDevice('video');

export type UserRole = 'admin' | 'owner' | 'unspecified' | 'producer' | 'viewer';
export const useUserRole = () => {
  const roomId = useRoom((room) => room.id);

  const userRoleMap = {
    [AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_ADMIN]: 'admin',
    [AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER]: 'owner',
    [AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_UNSPECIFIED]: 'unspecified',
    [AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_PRODUCER]: 'producer',
    [AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_VIEWER]: 'viewer'
  } as const;

  const role =
    useDepot((depotState) => depotState.roomRecords[roomId]?.permissions.role) ??
    AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_UNSPECIFIED;

  return userRoleMap[role] as UserRole;
};

/**
 * Check if the current user is admin or owner of the current room.
 * Works outside Room context using lastRoomIdPref.
 */
export const useIsAdminOrOwner = (): boolean => {
  const rooms = useDepot((depotState) => depotState.roomRecords, dequal);
  const [lastRoomID] = useLastRoomIdPref();
  const currentRoom = lastRoomID ? rooms[lastRoomID] : undefined;
  const role = currentRoom?.permissions?.role;

  return (
    !!role &&
    [
      AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_ADMIN,
      AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER
    ].includes(role)
  );
};
